
import React from 'react';
import { motion } from 'framer-motion';

interface LoadingSkeletonProps {
  type?: 'messages' | 'circle' | 'controls' | 'analysis';
  className?: string;
}

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({ 
  type = 'messages', 
  className = '' 
}) => {
  const shimmer = {
    animate: {
      backgroundPosition: ['200% 0', '-200% 0'],
    },
    transition: {
      repeat: Infinity,
      duration: 1.5,
      ease: 'linear',
    },
  };

  const skeletonClass = `
    bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 
    bg-[length:200%_100%] rounded animate-pulse
  `;

  if (type === 'messages') {
    return (
      <div className={`space-y-4 p-4 ${className}`}>
        {/* User message skeleton */}
        <div className="flex justify-end">
          <motion.div 
            {...shimmer}
            className={`${skeletonClass} h-12 w-3/4 rounded-2xl rounded-tr-sm`}
          />
        </div>
        
        {/* AI message skeleton */}
        <div className="flex justify-start">
          <div className="flex gap-3 w-3/4">
            <motion.div 
              {...shimmer}
              className={`${skeletonClass} h-8 w-8 rounded-full flex-shrink-0`}
            />
            <motion.div 
              {...shimmer}
              className={`${skeletonClass} h-16 flex-1 rounded-2xl rounded-tl-sm`}
            />
          </div>
        </div>
        
        {/* Another user message */}
        <div className="flex justify-end">
          <motion.div 
            {...shimmer}
            className={`${skeletonClass} h-8 w-1/2 rounded-2xl rounded-tr-sm`}
          />
        </div>
      </div>
    );
  }

  if (type === 'circle') {
    return (
      <div className={`flex justify-center ${className}`}>
        <motion.div 
          {...shimmer}
          className={`${skeletonClass} h-24 w-24 rounded-full`}
        />
      </div>
    );
  }

  if (type === 'controls') {
    return (
      <div className={`space-y-3 ${className}`}>
        <motion.div 
          {...shimmer}
          className={`${skeletonClass} h-10 w-full rounded-lg`}
        />
        <div className="flex gap-2">
          <motion.div 
            {...shimmer}
            className={`${skeletonClass} h-8 w-20 rounded-md`}
          />
          <motion.div 
            {...shimmer}
            className={`${skeletonClass} h-8 w-16 rounded-md`}
          />
        </div>
      </div>
    );
  }

  if (type === 'analysis') {
    return (
      <div className={`space-y-4 ${className}`}>
        <motion.div 
          {...shimmer}
          className={`${skeletonClass} h-6 w-1/3 rounded`}
        />
        <motion.div 
          {...shimmer}
          className={`${skeletonClass} h-4 w-full rounded`}
        />
        <motion.div 
          {...shimmer}
          className={`${skeletonClass} h-4 w-5/6 rounded`}
        />
        <motion.div 
          {...shimmer}
          className={`${skeletonClass} h-20 w-full rounded-lg`}
        />
      </div>
    );
  }

  return null;
};

export default LoadingSkeleton;
